import { offset, shift } from '@floating-ui/react-dom';
import { Images } from '@styled-icons/boxicons-regular';
import {
  backgroundColors,
  backgroundColorsBasic,
  ButtonElement,
  CLEAR_COLOR_NAME,
  colorsGradient,
  ContainerElement,
  DEFAULT_ACCENT_NAME,
} from '@typedream/core';
import { setNodes } from '@udecode/plate-core';
import classNames from 'classnames';
import {
  memo, useEffect, useRef, useState,
} from 'react';
import BackgroundSettings from '../../../../components/BackgroundSettings';
import { Floating } from '../../../../components/Floating';
import { useTPlateEditorRef } from '../../../../plate';
import { getSelectedElementEntry, useSelectedElementEntry } from '../../../../plugins/elementSelection';
import { useOutsideEvent } from '../../../../utils/eventAlerter';
import toolbarStyles from '../../SideToolbar.module.css';
import styles from './Background.module.css';
import { useDreamEditorStore } from '../../../../plugins/store';

type ElementWithBackground = ContainerElement;

const colorOptions = {
  [CLEAR_COLOR_NAME]: 'var(--background)',
  [DEFAULT_ACCENT_NAME]: backgroundColors[DEFAULT_ACCENT_NAME],
  ...backgroundColorsBasic,
  ...colorsGradient,
};

interface BackgroundProps {
  color?: boolean
  image?: boolean
  video?: boolean
}

function Background(props: BackgroundProps) {
  const editor = useTPlateEditorRef();

  const {
    color: hasBackgroundColorProperty,
    image: hasBackgroundImageProperty,
    video: hasBackgroundVideoProperty,
  } = props;

  const [element, path] = useSelectedElementEntry<ElementWithBackground>() || [];

  const [isReplacingBackground, setIsReplacingBackground] = useState(false);
  useEffect(() => setIsReplacingBackground(false), [path]);

  const floatingRef = useRef<Floating>(null);

  const ref = useOutsideEvent<HTMLDivElement>('mousedown', (event) => {
    if (
      floatingRef.current?.container === event.target
      || floatingRef.current?.container?.contains(event.target as Node)
    ) {
      return;
    }
    setIsReplacingBackground(false);
  });

  const colorKey = (element?.background
    || CLEAR_COLOR_NAME) as keyof typeof colorOptions;
  const colorValue = colorKey !== CLEAR_COLOR_NAME
    ? colorOptions[colorKey] || colorKey
    : undefined;

  const setBackgroundColor = (background: string) => {
    setNodes(
      editor,
      {
        background: background.trim(),
        backgroundImage: undefined,
        backgroundVideo: undefined,
        backgroundDarken: undefined,
      },
      { at: path },
    );
  };

  const setBackgroundImage = (url: string) => {
    setNodes(
      editor,
      {
        background: undefined,
        backgroundImage: url,
        backgroundVideo: undefined,
      },
      { at: path },
    );
  };

  const setBackgroundVideo = (url: string) => {
    setNodes(
      editor,
      {
        background: undefined,
        backgroundImage: undefined,
        backgroundVideo: url,
      },
      { at: path },
    );
  };

  const removeBackgroundMedia = () => {
    setNodes(
      editor,
      {
        background: 'default',
        backgroundImage: undefined,
        backgroundVideo: undefined,
        backgroundDarken: undefined,
      },
      { at: path },
    );
  };

  const toggleBackgroundDarken = () => {
    setNodes(
      editor,
      { backgroundDarken: !element?.backgroundDarken },
      { at: path },
    );
  };

  const buttonStyle = useDreamEditorStore(() => {
    const entry = getSelectedElementEntry<ButtonElement>(editor);
    if (!entry) return false;
    return entry?.[0].style === 'glowing_button';
  });

  return (
    <>
      <div ref={ref} className={toolbarStyles.section}>
        <span className={toolbarStyles.label}>
          {buttonStyle ? 'Border Color' : 'Background'}
        </span>
        <button
          type="button"
          className={styles.button}
          onClick={() => setIsReplacingBackground((v) => !v)}
        >
          {element?.backgroundImage || element?.backgroundVideo ? (
            <>
              <Images size={15} className={styles.mediaIcon} />
              {element.backgroundVideo ? 'Video' : 'Image'}
            </>
          ) : (
            <>
              <span
                className={classNames(styles.colorShowcase, {
                  [styles.withCrossLine]: colorKey === CLEAR_COLOR_NAME,
                })}
                style={{
                  background: colorValue,
                }}
              />
              {colorKey}
            </>
          )}
        </button>
      </div>

      <Floating
        ref={floatingRef}
        show={isReplacingBackground}
        reference={ref}
        options={{
          strategy: 'fixed',
          placement: 'left-start',
          middleware: [
            offset({ mainAxis: 32 }),
            shift({
              crossAxis: true,
              padding: { top: 8, bottom: 8 },
            }),
          ],
        }}
        autoUpdate
        className={styles.floating}
      >
        <BackgroundSettings
          currentValues={element || {}}
          colorOptions={colorOptions}
          onSelectColor={
            hasBackgroundColorProperty ? setBackgroundColor : undefined
          }
          onSelectImage={
            hasBackgroundImageProperty ? setBackgroundImage : undefined
          }
          onSelectVideo={
            hasBackgroundVideoProperty ? setBackgroundVideo : undefined
          }
          onRemoveBackgroundMedia={removeBackgroundMedia}
          onToggleDarken={toggleBackgroundDarken}
        />
      </Floating>
    </>
  );
}

Background.defaultProps = {
  color: false,
  image: false,
  video: false,
};

export default memo(Background);
